import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { WarningOctagon, PencilSimple } from "@phosphor-icons/react";

/**
 * v0.4.22 — Seller VAT registrations status banner. Extracted from
 * CalculatePanel to keep the parent component under the project's
 * 400-LOC hard cap.
 *
 * Renders one of two states:
 *   - Empty: amber warning + "Configure Settings" CTA. The
 *     calculation cannot be run; this is a hard gate.
 *   - Populated: subtle teal chip listing the registered country codes
 *     + "Edit in Settings" link.
 */
// A1-93 — the client selector is locked while a calculation is open; a
// disabled control says why. Rendered here because this block is the one
// that sits inside the client step (Step1Upload.layout.test pins that structure).
export const RegistrationsWarning = ({ clientLocked = false, ...props }) => {
  const { t } = useTranslation();
  return (
    <>
      <RegistrationsStatus {...props} />
      {clientLocked && (
        <p data-testid="client-locked-hint" className="px-4 pb-2 text-xs text-slate-500 dark:text-slate-400">
          {t("ui.calculate.client_locked_hint", { defaultValue: "The client is fixed while a calculation is open. Use Start over to change client." })}
        </p>
      )}
    </>
  );
};

const RegistrationsStatus = ({ registrations, settingsError }) => {
  const { t } = useTranslation();
  if ((registrations || []).length === 0) {
    return (
      <div
        data-testid="calc-registrations-warning"
        className="border-t border-amber-700/40 bg-amber-50 dark:bg-amber-900/40 px-4 py-3 flex items-start gap-3"
      >
        <WarningOctagon size={22} weight="bold" className="text-amber-600 dark:text-amber-400 mt-0.5" />
        <div className="flex-1">
          <div className="text-xs text-amber-700 dark:text-amber-300">
            {t("ui.calculate.registrations_empty_warning_title")}
          </div>
          <div className="tabular-nums text-[11px] text-amber-700/80 mt-1">
            {t("ui.calculate.registrations_empty_warning_body")}
          </div>
          {settingsError && (
            <div className="tabular-nums text-[11px] text-amber-700/70 mt-1">
              {settingsError}
            </div>
          )}
        </div>
        {/* v0.6.8 fix — was a plain <a href>; see the fix note on the
            other instance below for why that wipes the in-progress
            calc/review session. */}
        <Link
          to="/admin/clients"
          data-testid="calc-edit-settings-link"
          className="text-xs bg-amber-600/20 hover:bg-amber-600/30 border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 px-3 py-2 rounded-xl whitespace-nowrap"
        >
          {t("ui.calculate.configure_settings_link")}
        </Link>
      </div>
    );
  }
  return (
    <div
      data-testid="calc-seller-registrations"
      className="border-t border-slate-200 dark:border-slate-700 px-4 py-3 tabular-nums text-xs text-slate-500 dark:text-slate-400 flex items-center justify-between gap-3"
    >
      <span>
        <span className="text-[10px] tracking-normal text-slate-500 dark:text-slate-400">
          {t("ui.calculate.seller_registrations_label")}:
        </span>{" "}
        <span className="text-teal-700 dark:text-teal-300">{registrations.join(", ")}</span>
      </span>
      {/* v0.6.8 fix — found live 2026-09-07: this was a plain <a href>,
          which triggers a full BROWSER page reload instead of a
          client-side route change. That reload tears down the whole
          React app, including WorkflowContext's in-memory calc/review
          state — an operator clicking "Edit VAT registrations" from
          mid-review, making no changes, and returning found their
          calc results gone (had to re-run the calculation). <Link>
          preserves the SPA session across the navigation. */}
      <Link
        to="/admin/clients"
        data-testid="calc-edit-settings-link"
        className="inline-flex items-center gap-1.5 text-[11px] font-bold whitespace-nowrap bg-teal-600/15 hover:bg-teal-600/25 border border-teal-400/60 dark:border-teal-600/60 text-teal-700 dark:text-teal-300 px-3 py-1.5 rounded-lg transition-colors"
      >
        <PencilSimple size={12} weight="bold" />
        {t("ui.calculate.edit_in_settings")}
      </Link>
    </div>
  );
};
